/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, UserPic } from './UserPic';
import { CommonProps } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';

export const UserPicProps: PropertyDefinitions<
  Omit<UserPic, keyof CommonProps>
> = {
  userName: {
    label: 'User Name',
    type: 'expressionOnly',
    returnType: () => 'string',
  },
  size: {
    label: 'Size',
    type: 'number',
    min: 16,
    max: 120,
    step: 4,
  },
  showUserName: {
    label: 'Show User Name',
    type: 'boolean',
  },
  showEmailAddress: {
    label: 'Show Email Address',
    type: 'boolean',
  },
  hideOnlineStatus: {
    label: 'Hide Online Status',
    type: 'boolean',
  },
  hideImage: {
    label: 'Hide Image',
    type: 'boolean',
  },
};

const UserPicManager: ComponentManagerFn<'UserPic'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'UserPic'>(platform, 'UserPic'),
    type: 'UserPic',
    name: 'User Pic',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Box'] as ItemType[]).includes(childType),
    defaultValue,
    props: UserPicProps,
    getDisplayText: (item) => item.props.userName,
    category: 'Dashboard',
    icon: { iconName: 'user' },
    description: `**Overview**<br/>
The User Pic component is a standardized identity widget designed to display a user's profile image or fallback initials. In most applications, showing "who" is performing an action (like a logged-in user, a record owner, or a commenter) requires conditional logic to check if a profile photo exists.
This component handles that complexity automatically. It takes a user identifier and renders a polished avatar—showing the image if available, or generating styled initials if not—ensuring a consistent look across your application without manual configuration.

**1. Identity Source**<br/>
The component relies on a single key input to resolve the user's identity.
* **User Name:** An expression that resolves to the target user's unique name or identifier (e.g., \`$.session.userName\` for the current user, or \`$.record.owner\` for a data item).
* **Behavior:** The system uses this value to fetch the associated profile metadata (image, email, online status). If no image is found, the system uses the first letters of this name to generate the fallback initials.

**2. Visual Presentation**<br/>
You can adapt the avatar's appearance to fit different contexts, from a tiny table icon to a large profile header.
* **Size:** Controls the diameter of the circular avatar in pixels (Range: 16px to 120px).
* **Hide Image:** A boolean toggle that suppresses the profile photo even if one exists. (Use Case: Enable this to force a consistent "Initials Only" look for lists where mixed images and text might look cluttered).
* **Hide Online Status:** By default, the component may show a small "presence" indicator (green dot). Enable this property to remove that indicator for a cleaner, static appearance.

**3. Contextual Details**<br/>
While the avatar is purely visual, you can optionally append text information directly next to it, creating a complete "User Card" without needing extra layout components.
* **Show User Name:** Displays the full display name of the user to the right of the avatar.
* **Show Email Address:** Displays the user's email address below or next to the name.
* **Tip:** Enabling these properties effectively turns the User Pic into a List Item or Header, perfect for navigation menus or "Assigned To" fields.

**4. Interactivity**<br/>
The User Pic is often used as a gateway to user-specific actions.
* **TriggerOnClick:** The component supports adding a TriggerOnClick child. Use this to create interactive avatars—for example, clicking the top-right profile picture to open the "User Settings" menu or clicking a record owner's face to view their full profile.`,
    helpRenders: [
      {
        title: 'User Pic',
        description: 'User Pic for the userName of the current session.',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['userName', 'size'],
        item: {
          itemId: 'a1',
          props: {
            userName: '$.session.userName',
          },
          children: [],
          type: 'UserPic',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default UserPicManager;
